import { trpc } from "@/lib/trpc";
import { useChatStore } from "@/stores/chat-store";
import { useUpdateMessageMutation } from "../mutations/MessageMutations";
import { LLMStreamEvent } from "@shared/types/message";

export const useOnLLMStream = (
    threadId: string,
    setIsProcessing?: (isStreaming: boolean) => void,
    setIsStreaming?: (isStreaming: boolean) => void,
    streamingStoppedRef?: React.MutableRefObject<boolean>
) => {
    const { filters } = useChatStore();
    const utils = trpc.useUtils();
    const { mutate: updateMessage } = useUpdateMessageMutation();
    return (trpc as any).stream.onStream.useSubscription(
        { threadId },
        {
            onData: ({ data }: { data: LLMStreamEvent; id: string }) => {
                // Check if streaming has been stopped - ignore chunks EXCEPT completion events
                if (streamingStoppedRef?.current && !data.isComplete) {
                    return;
                }

                // Handle completion event
                if (data.isComplete) {
                    setIsProcessing?.(false);
                    setIsStreaming?.(false);

                    const previousMessages = (utils as any).messages.findMany.getInfiniteData({
                        ...filters,
                        threadId,
                    });

                    if (previousMessages) {
                        const fullAssistantMessage = previousMessages.pages
                            .map((page: any) => page.data)
                            .flat()
                            .find((message: any) => message.id === data.messageId);

                        if (fullAssistantMessage) {
                            const content = fullAssistantMessage.content + data.content;
                            console.log("Updating message with metadata", {
                                messageId: data.messageId,
                                endTime: (data.metadata as any)?.agentProcessing?.endTime,
                            });
                            updateMessage({ id: data.messageId, data: { content, metadata: data.metadata } });
                        } else {
                            console.warn("Could not find message to update", data.messageId);
                        }
                    }
                    return;
                }

                // For non-completion events, set streaming state
                setIsProcessing?.(false);
                setIsStreaming?.(true);

                (utils as any).messages.findMany.cancel({ threadId });

                // if (data.type === "final_answer" && !data.isComplete && !data.content) {
                //     return; // Continue streaming but don't update content
                // }

                const previousMessages = (utils as any).messages.findMany.getInfiniteData({
                    ...filters,
                    threadId,
                });

                // Update the message content in the cache
                (utils as any).messages.findMany.setInfiniteData(
                    {
                        ...filters,
                        threadId,
                    },
                    (oldData: any) => {
                        if (!oldData) return oldData;

                        let messageFound = false;
                        const pages = oldData.pages.map((page: any) => {
                            page.data = page.data.map((message: any) => {
                                // Use the correct message ID from the stream data
                                if (message.id === data.messageId) {
                                    messageFound = true;
                                    // Only append content if it's not undefined/null
                                    let content = message.content || "";
                                    if (data.content !== undefined && data.content !== null) {
                                        content = content + data.content;
                                    }
                                    // Handle empty content case
                                    if (!content) content = " ";
                                    return {
                                        ...message,
                                        metadata: data.metadata,
                                        content,
                                    };
                                }
                                return message;
                            });
                            return page;
                        });

                        // If message wasn't found in the cache (e.g. race condition where stream starts before refetch),
                        // add it manually to the first page
                        if (!messageFound && pages.length > 0) {
                            const newMessage = {
                                id: data.messageId,
                                threadId,
                                role: "assistant",
                                content: data.content || " ",
                                metadata: data.metadata,
                                createdAt: new Date().toISOString(),
                                updatedAt: new Date().toISOString(),
                            };
                            
                            // Add to the beginning of the first page
                            pages[0].data = [newMessage, ...pages[0].data];
                            // Update count if it exists
                            if (typeof pages[0].count === 'number') {
                                pages[0].count += 1;
                            }
                        }

                        return {
                            pages,
                            pageParams: oldData.pageParams,
                        };
                    }
                );
                return { previousMessages };
            },
            onError: () => {
                setIsStreaming?.(false);
            },
        }
    );
};
